import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Heart, Shield, Smartphone, Star, Clock, Gift } from 'lucide-react';

const CreatorCTA = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-purple-600 via-purple-700 to-pink-600 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid-white/[0.1] [mask-image:linear-gradient(0deg,transparent,black,transparent)]" />
      
      <div className="container mx-auto px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="text-center max-w-4xl mx-auto"
        >
          {/* Special Offer Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-8"
          >
            <Badge className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-6 py-3 text-lg font-bold">
              <Gift className="h-5 w-5 mr-2" />
              Limited Time: 14-Day Free Trial!
            </Badge>
          </motion.div>

          {/* Main Headline */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
          >
            Your Family's Memories
            <br />
            <span className="bg-gradient-to-r from-yellow-300 to-yellow-400 bg-clip-text text-transparent">
              Deserve Better
            </span>
          </motion.h2>

          {/* Sub-headline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl text-purple-100 mb-8 leading-relaxed"
          >
            Stop losing precious moments in crowded camera rolls.
            <br />
            Start sharing them safely with the people who matter most.
          </motion.p>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-wrap items-center justify-center gap-8 mb-12 text-purple-200"
          >
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              <span>Private & Secure</span>
            </div>
            <div className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              <span>Mobile First</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="h-5 w-5 fill-current" />
              <span>Built for Families</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="h-5 w-5 fill-current" />
              <span>4.9★ Rating</span>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8"
          >
            <Button
              size="lg"
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 px-8 py-4 text-xl font-bold shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105"
            >
              Start Free Trial - Then $9/month
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            
            <Button
              variant="outline"
              size="lg"
              className="border-2 border-white text-white hover:bg-white hover:text-purple-600 px-8 py-4 text-xl font-bold bg-transparent"
            >
              <Smartphone className="h-5 w-5 mr-2" />
              See How It Works
            </Button>
          </motion.div>

          {/* Final Reassurance */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-purple-200 space-y-2"
          >
            <p className="text-lg">
              ✨ 14-day free trial • 💳 No credit card required
            </p>
            <p className="text-lg">
              🔒 Cancel anytime • 📱 Works on iPhone & web
            </p>
          </motion.div>
        </motion.div>

        {/* Value Proposition Cards */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.9 }}
          className="mt-20 max-w-6xl mx-auto"
        >
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 border border-white border-opacity-20">
              <div className="text-center">
                <div className="bg-green-500 rounded-full p-3 w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                  <Heart className="h-6 w-6 text-white fill-current" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Family First</h3>
                <p className="text-purple-100 text-sm">
                  Designed specifically for families who want to share precious moments safely, without ads or strangers.
                </p>
              </div>
            </div>
            
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 border border-white border-opacity-20">
              <div className="text-center">
                <div className="bg-blue-500 rounded-full p-3 w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                  <Smartphone className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Super Simple</h3>
                <p className="text-purple-100 text-sm">
                  Upload from your phone in seconds. Grandparents can watch with just a click. No tech headaches.
                </p>
              </div>
            </div>
            
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 border border-white border-opacity-20">
              <div className="text-center">
                <div className="bg-yellow-500 rounded-full p-3 w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                  <Star className="h-6 w-6 text-white fill-current" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Worth Every Penny</h3>
                <p className="text-purple-100 text-sm">
                  Less than two coffee shop visits per month for unlimited family memories, organized and safe forever.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Urgency Reminder */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 rounded-2xl p-6 max-w-2xl mx-auto">
            <Clock className="h-8 w-8 mx-auto mb-3" />
            <p className="font-bold text-lg mb-2">
              🎯 Don't Wait - Your Memories Can't
            </p>
            <p className="text-sm">
              Every day you wait is another day of precious moments stuck in your camera roll, unseen by the grandparents and family who love your kids.
            </p>
          </div>
        </motion.div>

        {/* Social Proof */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="text-center mt-12"
        >
          <div className="text-purple-200 text-sm">
            <p>Join 2,500+ families already sharing safely with HiveShare Creator</p>
            <div className="flex items-center justify-center mt-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 text-yellow-400 fill-current mx-0.5" />
              ))}
              <span className="ml-2">4.9/5 from happy families</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CreatorCTA;